/**
 * Copyright © 2026 Adnuntius AS.
 */
import {AdnListHelperService} from '../common/controller/list-helper.service';
import {ChangeDetectorRef, Component, Inject, NgZone, OnChanges} from '@angular/core';
import * as _ from 'lodash';
import template from './blocklist-edit-page.component.html';
import {AppEventBusService} from '../common/events/app-event-bus.service';

import {AdnSelectSearchElement} from '../common/directives/adn-select/adn-select.component';
import {AdminResourceEditPageBase} from '../common/controller/admin-resource-edit-page-base';
import {normalizeIabCategoryIds} from '../../components/util/iab-category-search';
import {TeamResource} from '../../components/api/resources/resource/team.resource';
import {AdUnitResource} from '../../components/api/resources/resource/adunit.resource';
import {EarningsAccountResource} from '../../components/api/resources/resource/earnings-account.resource';
import {SiteResource} from '../../components/api/resources/resource/site.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {SearchResource} from '../../components/api/resources/resource/search';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';

@Component({
  standalone: false,
  selector: 'adn-blocklist-edit-page',
  template
})
export class BlocklistEditPageComponent extends AdminResourceEditPageBase implements OnChanges {
  readonly usageLists: {publishers: any; sites: any; adUnits: any} = {
    publishers: {},
    sites: {},
    adUnits: {}
  };

  allTeams: any[] | null = null;
  canUpdateBlocklist = false;
  advertiserSearchResults: any[] = [];
  lineItemSearchResults: any[] = [];
  creativeSearchResults: any[] = [];

  private listInitialized = false;

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) LocalUserPermissions: any,
    private adnListHelper: AdnListHelperService,
    @Inject(EarningsAccountResource) private EarningsAccount: any,
    @Inject(SiteResource) private Site: any,
    @Inject(AdUnitResource) private AdUnit: any,
    @Inject(TeamResource) private Team: any,
    @Inject(SearchResource) private searchResource: any,
    private cdr: ChangeDetectorRef,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
    this.canUpdateBlocklist = LocalUserPermissions.hasAny('AX_PUBLISHER') || LocalUserPermissions.hasAny('MANAGE_PUBLISHING');
    this.deferFormDirtyTracking();

    this.Team.query({minimal: true, teamTypes: 'STANDARD;AX_PUBLISHER'}).$promise.then((page: any) => {
      // null → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferMarkForCheck(this.cdr, () => {
        this.allTeams = _.filter(page.results, (team: any) => {
          return team.type === 'STANDARD' || team.type === 'AX_PUBLISHER';
        });
        this.resolveTeamReference();
        this.resumeFormDirtyTracking();
      });
    });
  }

  ngOnChanges(): void {
    super.ngOnChanges();
    if (this.model?.id && !this.listInitialized) {
      this.listInitialized = true;
      const moreParams = {blocklist: this.model.id};
      this.adnListHelper.setUpBasicList(this.usageLists.publishers, this.EarningsAccount, 'fullPublishersForBlocklist', {
        moreParams,
        baseTable: 'earningsAccount'
      });
      this.adnListHelper.setUpBasicList(this.usageLists.sites, this.Site, 'fullSitesForBlocklist', {
        moreParams,
        baseTable: 'site'
      });
      this.adnListHelper.setUpBasicList(this.usageLists.adUnits, this.AdUnit, 'fullAdUnitsForBlocklist', {
        moreParams,
        baseTable: 'adUnit'
      });
      if (!this.isNew) {
        this.usageLists.publishers.makeQuery?.();
        this.usageLists.sites.makeQuery?.();
        this.usageLists.adUnits.makeQuery?.();
      }
    }
  }

  protected afterInitEditingModel(): void {
    this.editingModel.iabCategoryAction = this.editingModel.iabCategoryAction || 'BLOCKED';
    this.editingModel.iabCategories = normalizeIabCategoryIds(this.editingModel.iabCategories);
    this.editingModel.advertiserDomains = _.map(this.editingModel.advertiserDomains || [], (domain: any) => {
      return _.get(domain, 'text', domain);
    });
    this.resolveTeamReference();
  }

  protected afterInitAfterSave(): void {
    this.editingModel.team = this.editingModel.team || undefined;
    this.editingModel.iabCategoryAction = this.editingModel.iabCategoryAction || 'BLOCKED';
    this.resolveTeamReference();
    if (!this.isNew) {
      this.usageLists.publishers.makeQuery?.();
      this.usageLists.sites.makeQuery?.();
      this.usageLists.adUnits.makeQuery?.();
    }
  }

  protected onBeforeSubmit(): void {
    this.editingModel.advertiserDomains = _.map(this.editingModel.advertiserDomains || [], (domain: any) => {
      return _.get(domain, 'text', domain);
    });
    this.editingModel.iabCategories = normalizeIabCategoryIds(this.editingModel.iabCategories);
  }

  onFieldChange(): void {
    this.markExternalFormDirty();
  }

  advertiserSearch = (searchElement: AdnSelectSearchElement): void => {
    this.searchResource.query({q: searchElement.search, types: 'Advertiser'}).then((data: any) => {
      this.ngZone.run(() => {
        this.advertiserSearchResults = data.searchResults;
      });
    });
  };

  lineItemSearch = (searchElement: AdnSelectSearchElement): void => {
    this.searchResource.query({q: searchElement.search, types: 'LineItem'}).then((data: any) => {
      this.ngZone.run(() => {
        this.lineItemSearchResults = data.searchResults;
      });
    });
  };

  creativeSearch = (searchElement: AdnSelectSearchElement): void => {
    this.searchResource.query({q: searchElement.search, types: 'Creative'}).then((data: any) => {
      this.ngZone.run(() => {
        this.creativeSearchResults = data.searchResults;
      });
    });
  };

  private resolveTeamReference(): void {
    if (!this.editingModel?.team || !this.allTeams?.length) {
      return;
    }
    const teamId = _.isString(this.editingModel.team) ? this.editingModel.team : this.editingModel.team.id;
    const match = _.find(this.allTeams, ['id', teamId]);
    if (match) {
      this.editingModel.team = match;
    }
  }
}
